/**
 * @file Container component for player item with swipe actions and navigation
 * @remarks
 * Manages player item state, actions, and navigation. Swipe actions navigate
 * to the player detail page and set that player's tab via selectPlayerTab:
 * - Search (blue): opens player page on "Similar Players" tab
 * - Compare (green): opens player page on "DataHub" tab
 */

import { useReactiveVar } from "@apollo/client";
import { differenceInMonths } from "date-fns";
import { Href, useRouter } from "expo-router";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Alert, Platform } from "react-native";

import PlayerItemView from "./PlayerItemView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { IInfoBadge } from "@/components/organisms/InfoPlayer/InfoPlayerView";
import { useCurrentUser } from "@/features/auth";
import {
  CurrencyCode,
  SortField,
  WatchlistListMyUserWatchlistsDocument,
  WatchlistListMyUserWatchlistsQuery,
  WatchlistWatchlistMetaFragment,
  useWatchlistRemovePlayerFromWatchlistMutation,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useTheme } from "@/providers/ThemeProvider";
import { playerDetailVar, selectPlayerTab } from "@/stores/player";
import { watchlistShowVar, watchlistSortVar } from "@/stores/selectionSort";
import {
  extractCustomMetricId,
  isCustomMetricSort,
} from "@/utils/customMetricsSorting";
import {
  changeStatusPlayerInWatchlist,
  modifyWatchlistEntityPlayers,
} from "@/utils/handleWatchlistPlayers";

/**
 * Props for PlayerItemContainer component
 */
interface Props {
  /** Player object containing profile and performance data */
  item: PlayerCardData;
  /** Watchlist containing the player (if any) */
  watchlist?: WatchlistWatchlistMetaFragment | null;
  /** Whether item is currently being dragged */
  isActive: boolean;
  /** Handler for drag-and-drop reordering */
  drag?: () => void;
}

/**
 * Container component for player item with actions, metrics, and navigation
 * @param root0 - Component props
 * @param root0.item - Player object containing profile and performance data
 * @param root0.watchlist - Watchlist containing the player (if any)
 * @param root0.drag - Handler for drag-and-drop reordering
 * @param root0.isActive - Whether item is currently being dragged
 */
const PlayerItemContainer = ({ item, watchlist, drag, isActive }: Props) => {
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as React.RefObject<CustomPromptRefProps>;
  const [showActionsheet, setShowActionsheet] = useState(false);
  const watchlistShowsSetting = useReactiveVar(watchlistShowVar);
  const sortValues = useReactiveVar(watchlistSortVar);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const currentUser = useCurrentUser();
  const currencyCode =
    currentUser?.organization?.currencyCode ?? CurrencyCode.Eur;

  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);
  const [removePlayerMutation, { loading }] =
    useWatchlistRemovePlayerFromWatchlistMutation();

  const router = useRouter();

  const playerUrl = useExpandedPlayerUrl(`/players/${item.id}`);

  const handleSearch = useCallback(() => {
    // SE-8285: name the player the tab is chosen for. The write happens before
    // the navigation, so the selection is already waiting when the target
    // player's header renders.
    selectPlayerTab(item.id, "similarPlayers");
    router.push(playerUrl as Href);
  }, [router, playerUrl, item.id]);

  const handleAddPress = useCallback(() => {
    setShowActionsheet(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowActionsheet(false);
  }, []);

  const { handleAddWatchlistPress } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const recordRemovePlayerGoal = useRecordUserGoal({
    goalType: GoalType.RemovePlayerFromWatchlist,
    value: item.id,
  });

  const removePlayer = useCallback(() => {
    const watchlistId = watchlist?.id;
    if (!watchlistId) {
      Alert.alert("Error", "Unable to remove player. Please try again.");
      return Promise.resolve();
    }

    return removePlayerMutation({
      onCompleted: () => {
        recordRemovePlayerGoal();
      },
      onError: error => {
        console.error("[Watchlist] Mutation error:", error);
        Alert.alert(
          "Error",
          "Failed to remove player from watchlist. Please try again."
        );
      },
      variables: {
        input: { playerId: item.id, watchlistId },
      },
      refetchQueries: ["WatchlistListWatchlistPlayers"],
      update: (cache, { data: dataCache, errors }) => {
        if (!dataCache?.removePlayerFromWatchlist || errors) {
          return;
        }
        // SE-6777 - `changeStatusPlayerInWatchlist` only writes
        // `Player.isWatchedByCurrentUser`, and `WatchlistListWatchlistPlayers`
        // selects no `playerCount`, so the count badge stayed stale. The entity
        // write runs BEFORE the membership sync: that sync is skipped whenever
        // `WatchlistListMyUserWatchlists` is not cached, and the badge must not
        // depend on an unrelated query being present.
        modifyWatchlistEntityPlayers(cache, watchlistId, item.id, "remove");
        const data: WatchlistListMyUserWatchlistsQuery | null = cache.readQuery(
          { query: WatchlistListMyUserWatchlistsDocument }
        );
        if (!data) {
          return;
        }
        changeStatusPlayerInWatchlist(cache, item.id, data);
      },
    });
  }, [item.id, watchlist, removePlayerMutation, recordRemovePlayerGoal]);

  const handleRemovePress = useCallback(() => {
    const playerName = item?.name ?? "";
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to remove ${playerName}?`
      );
      return doDelete ? removePlayer() : null;
    } else {
      return new Promise((resolve, reject) =>
        Alert.alert(
          `Remove ${playerName}`,
          `Are you sure you want to remove ${playerName}?`,
          [
            { text: "Cancel", style: "cancel" },
            {
              text: "Yes",
              onPress: () => removePlayer().then(resolve).catch(reject),
              style: "destructive",
            },
          ]
        )
      );
    }
  }, [item, removePlayer]);

  const isPlayerInAnyWatchlist = Boolean(item.isWatchedByCurrentUser);

  const handlePress = useCallback(() => {
    router.push(playerUrl as Href);
  }, [playerUrl, router]);

  const infoBadge: IInfoBadge = useMemo(() => {
    // Use sortValues if available and custom, otherwise fallback to watchlistShowsSetting
    const currentSortField = sortValues?.sortBy || watchlistShowsSetting.type;
    const currentSortFieldStr = String(currentSortField);

    // Check if current sort is a custom metric
    if (isCustomMetricSort(currentSortFieldStr)) {
      const customMetricId = extractCustomMetricId(currentSortFieldStr);
      const customStats = item?.bioData?.customStats;
      const customValue = customStats?.[customMetricId];

      return {
        value: customValue ? parseFloat(customValue.value) : null,
        type: currentSortField as SortField,
        min: 0,
        max: 100,
      };
    }

    // Handle standard fields
    switch (currentSortField) {
      case SortField.FitScore:
        return {
          value: item?.bioData?.fitScore,
          type: SortField.FitScore,
          min: 40,
          max: 100,
        };
      case SortField.TeamStyleFit:
        return {
          value: item?.bioData?.teamStyleFit,
          type: SortField.TeamStyleFit,
          min: 30,
          max: 100,
        };
      case SortField.Carrying:
        return {
          value: item?.bioData?.carryingTransGlobalCategoricalScore,
          type: SortField.Carrying,
          min: 40,
          max: 85,
        };
      case SortField.Dribbling:
        return {
          value: item?.bioData?.dribblingTransGlobalCategoricalScore,
          type: SortField.Dribbling,
          min: 40,
          max: 85,
        };
      case SortField.Passing:
        return {
          value: item?.bioData?.passingTransGlobalCategoricalScore,
          type: SortField.Passing,
          min: 40,
          max: 85,
        };
      case SortField.Shooting:
        return {
          value: item?.bioData?.shotLocationTransGlobalCategoricalScore,
          type: SortField.Shooting,
          min: 40,
          max: 85,
        };
      case SortField.GkPassing:
        return {
          value: item?.bioData?.gkPassingTransCategoricalScore,
          type: SortField.GkPassing,
          min: 40,
          max: 85,
        };
      case SortField.GkPositioning:
        return {
          value: item?.bioData?.gkPositioningTransCategoricalScore,
          type: SortField.GkPositioning,
          min: 40,
          max: 85,
        };
      case SortField.GkShotStopping:
        return {
          value: item?.bioData?.gkShotStoppingTransCategoricalScore,
          type: SortField.GkShotStopping,
          min: 40,
          max: 85,
        };
      case SortField.PlayerValuation:
        return {
          value: item?.bioData?.playerValuation,
          type: SortField.PlayerValuation,
          min: 0,
          max: 1000000000,
          isDefaultStyle: true,
        };
      case SortField.MonthsRemaining:
        if (item?.bioData?.contractExpires) {
          const contractExpires = new Date(
            item?.bioData?.contractExpires
          ).toISOString();
          return {
            value: differenceInMonths(contractExpires, new Date()),
            type: SortField.MonthsRemaining,
            min: 18,
            max: 6,
          };
        }
        return {
          value: null,
          type: SortField.MonthsRemaining,
          min: 18,
          max: 6,
        };
      default:
        return {
          value: item?.bioData?.gpr,
          type: SortField.Gpr,
          min: 30,
          max: 100,
        };
    }
  }, [item.bioData, watchlistShowsSetting, sortValues]);

  const playerName = useMemo(() => item?.name ?? "", [item]);
  const handlePressViewItem = useCallback(() => {
    // Navigate to player page and update URL
    router.push(playerUrl as Href);
    // Update side panel with player details
    playerDetailVar({
      player: item,
      type: watchlist ? "watchList" : "roster",
    });
  }, [router, playerUrl, item, watchlist]);

  const handleCompare = useCallback(() => {
    // SE-8285: same pre-navigation write, naming the player it is about.
    selectPlayerTab(item.id, "dataHub");
    router.push(playerUrl as Href);
  }, [router, playerUrl, item.id]);

  return (
    <PlayerItemView
      onPress={handlePress}
      item={item}
      loading={loading}
      onDelete={watchlist ? handleRemovePress : null}
      isActive={isActive}
      isSmallScreen={isSmallScreen}
      drag={drag}
      onSearch={handleSearch}
      onAdd={handleAddPress}
      showActionsheet={showActionsheet}
      onClose={handleClose}
      onAddWatchlistPress={handleAddWatchlistPress}
      isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
      infoBadge={infoBadge}
      playerName={playerName}
      onCompare={handleCompare}
      promptRef={promptRef}
      currencyCode={currencyCode}
      onPressViewItem={handlePressViewItem}
      isDark={isDark}
    />
  );
};

export default PlayerItemContainer;
